import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JoinRequestDetail } from 'src/api/joinRequests';
import { api } from 'src/common/utils/api';

type PatchPayload = {
  companyName?: string;
  tenantName?: string;
  frn?: string | null;
  registeredAddress?: string;
  trialEndDate?: string | null;
};

export const usePatchJoinRequest = (id: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, reset } = useMutation<{ data: unknown }, Error, PatchPayload>({
    mutationFn: patch => api.patch(`admin/join-requests/${id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
    },
  });
  return { patchRequest: mutateAsync, isPending, reset };
};

export const useRejectJoinRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, reset } = useMutation<
    { data: unknown },
    Error,
    { id: string; reason?: string }
  >({
    mutationFn: ({ id, reason }) =>
      api.post(`admin/join-requests/${id}/reject`, { rejectionReason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
    },
  });
  return { rejectRequest: mutateAsync, isPending, reset };
};

export const useProvisionJoinRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, reset } = useMutation<{ data: JoinRequestDetail }, Error, string>(
    {
      mutationFn: id =>
        api.post<JoinRequestDetail>(`admin/join-requests/${id}/provision`, undefined, {
          timeoutMs: 120_000,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
      },
    },
  );
  return { provisionRequest: mutateAsync, isPending, reset };
};

export const useInviteUserJoinRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, reset } = useMutation<{ data: JoinRequestDetail }, Error, string>(
    {
      mutationFn: id =>
        api.post<JoinRequestDetail>(`admin/join-requests/${id}/invite-user`, undefined),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
      },
    },
  );
  return { inviteUserRequest: mutateAsync, isPending, reset };
};

export const useCompleteJoinRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, reset } = useMutation<
    { data: JoinRequestDetail },
    Error,
    { id: string; sendDocuSign: boolean }
  >({
    mutationFn: ({ id, sendDocuSign }) =>
      api.post<JoinRequestDetail>(`admin/join-requests/${id}/complete`, { sendDocuSign }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
    },
  });
  return { completeRequest: mutateAsync, isPending, reset };
};
